import * as Sentry from "@sentry/react"
import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import { HorizontalSpacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { useInviteMembersToGroup } from "../../../hooks/useinviteMembersToGroup"
import { useSuggestedMembers } from "../../../hooks/useSuggestedMembers"
import { formatMembers } from "../../../utils/formatters"
import { checkEmailValid } from "../../../utils/helpers"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SelectRoleDropdown from "../../atoms/inputs/SelectRoleDropdown"
import AddMemberCard from "../AddMemberCard"
import TextInput from "../TextInput"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface Props {
  groupName: string
  groupId: string | number
  orgId: string | number
}

const ContentContainer = styled.div`
  max-height: 60vh;
  overflow-y: scroll;
`

const InviteMemberToGroupModal: React.FunctionComponent<Props> = ({ groupName, groupId, orgId }) => {
  const { suggestedUsers } = useSuggestedMembers(groupId ?? 1)
  const formattedMembers = formatMembers(suggestedUsers ?? [])
  const [invitedMembers, setInvitedMembers] = React.useState<Array<{ email: string; role: string }>>([])
  const [emailValue, setEmailValue] = React.useState<string>("")
  const [selectedRole, setSelectedRole] = React.useState<string>("Viewer")
  const [successfullyInvited, setSuccessfullyInvited] = React.useState(false)

  const onClickAddSuggestedMember = (role: string, email?: string) => {
    if (!email || email === "error") {
      Sentry.captureMessage("Error, user missing email on invite")
    } else {
      setInvitedMembers([...invitedMembers, { email: email, role: role }])
      onSendInvite(email, role)
    }
  }

  const onClickSendInvite = () => {
    if (checkEmailValid(emailValue ?? "")) {
      console.log("Send invite to ", emailValue)
      onSendInvite(emailValue, selectedRole)
    } else {
      alert("Invalid email format")
    }
  }

  const onSelectRole = (role: string) => {
    setSelectedRole(role)
  }

  const onCompletedSendInviteToMember = () => {
    console.log("Sent invite to member")
    setSelectedRole("Viewer")
    setEmailValue("")
    setSuccessfullyInvited(true)
    setTimeout(() => setSuccessfullyInvited(false), 2000)
  }
  const onErrorSendInviteToMember = (error: ApolloError) => {
    captureExceptionWithContext(error, "error sending invite to member")
  }
  const { inviteMembersToGroup } = useInviteMembersToGroup({
    onCompleted: onCompletedSendInviteToMember,
    onError: onErrorSendInviteToMember,
  })
  const onSendInvite = async (email: string, role: string) => {
    try {
      await inviteMembersToGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          users: [{ email: email, role: role }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error sending invite to member")
    }
  }

  const checkMemberIsAdded = (email: string) => {
    const res = invitedMembers.find((e) => e.email === email)
    return res ? true : false
  }

  return (
    <div>
      <Row>
        <Col md={6}>
          <Subtitle>Invite a new member to the group {groupName}</Subtitle>
          <br />
          <H3>
            New members get an invite through email and has to accept before being added to a group or organizaton.
          </H3>
          <br />
          <br />
          <TextInput
            textFieldTitle="Email"
            placeholder="Email address of the person you want to invite"
            onChange={(e) => setEmailValue(e.target.value)}
            value={emailValue}
          />
          <SelectRoleDropdown title="Select group role: " onChange={onSelectRole} defaultValue={selectedRole} />
          <HorizontalSpacer spacing={2} />
          <PrimaryButton title="Send invite" onClick={onClickSendInvite} />
          {successfullyInvited && <H3 color={theme.colors.greenDark}>Invite sent</H3>}
        </Col>
        <Col md={6}>
          <H3>Suggested members:</H3>
          <HorizontalSpacer spacing={1} />
          <ContentContainer>
            {formattedMembers.map((member) => {
              return (
                <AddMemberCard
                  key={member.member.id}
                  member={member}
                  onClickAdd={onClickAddSuggestedMember}
                  isAdded={checkMemberIsAdded(member.member.email)}
                  onClickRemoveAddedMember={() => console.log("uninvite this bitch")}
                />
              )
            })}
          </ContentContainer>
        </Col>
      </Row>
    </div>
  )
}

export default InviteMemberToGroupModal
